import {
  type MembershipPaymentFault,
  type MembershipUpstream,
  paymentFaultOf,
} from './errors';
import type { MembershipFormatters } from './format';
import type { MembershipLine } from './lines';
import type { Membership, MembershipPlan, MembershipStatus } from './types';
import { coversZone } from './view';

/** Which buttons the member card shows. */
export interface MembershipMemberActions {
  changePlan: boolean;
  /** "Update card": the main action while `past_due`, a secondary one otherwise. */
  updateCard: 'primary' | 'secondary' | null;
  cancel: boolean;
  /** "Keep current plan" next to a scheduled switch. */
  keepPlan: boolean;
}

const CANCELLABLE: ReadonlySet<MembershipStatus> = new Set([
  'trialing',
  'active',
  'past_due',
]);

const isRunning = (membership: Membership): boolean =>
  membership.status === 'trialing' || membership.status === 'active';

/**
 * Buttons of the member card. A membership granted from the CMS has no card,
 * so it can only be cancelled; a cancelled one shows no buttons at all.
 */
export const memberActions = (
  membership: Membership,
  hasOtherPlans: boolean,
): MembershipMemberActions => {
  const isSelf = membership.source === 'self';
  const isScheduled = membership.scheduledChange !== null;
  const running = isRunning(membership);
  let updateCard: MembershipMemberActions['updateCard'] = null;
  if (isSelf && membership.status === 'past_due') {
    updateCard = 'primary';
  } else if (isSelf && running) {
    updateCard = 'secondary';
  }
  return {
    changePlan: isSelf && running && !isScheduled && hasOtherPlans,
    updateCard,
    cancel: CANCELLABLE.has(membership.status),
    keepPlan: isSelf && running && isScheduled,
  };
};

/**
 * A plan switch is allowed at all: a bought membership that renews and has
 * no switch scheduled. Whether there is a plan to switch to is the caller's.
 */
export const canChangePlan = (membership: Membership): boolean =>
  memberActions(membership, true).changePlan;

/** The card of a membership the customer bought can be replaced while it renews or is overdue. */
export const canUpdateCard = (membership: Membership): boolean =>
  memberActions(membership, false).updateCard !== null;

/** End of the period being used: the trial end in a trial, the paid period end otherwise. */
export const periodEnd = (membership: Membership): string | null =>
  membership.status === 'trialing'
    ? membership.trialEnd
    : membership.currentPeriodEnd;

/**
 * Until when a cancelled membership keeps its benefits: the trial end when it
 * was cancelled during the trial, the paid period end otherwise.
 */
export const benefitsEnd = (membership: Membership): string | null => {
  const { trialEnd, cancelledAt, currentPeriodEnd } = membership;
  const cancelledInTrial =
    trialEnd !== null &&
    cancelledAt !== null &&
    Date.parse(cancelledAt) < Date.parse(trialEnd);
  return cancelledInTrial ? trialEnd : currentPeriodEnd;
};

/** Plans of the current zone the member can switch to: every one but the current and the scheduled. */
export const switchablePlans = (
  membership: Membership,
  plans: MembershipPlan[],
): MembershipPlan[] =>
  plans.filter(
    (plan) =>
      plan.id !== membership.plan.id &&
      plan.id !== membership.scheduledChange?.planId,
  );

/** How a plan switch takes effect: an upgrade at once, a downgrade at the period end. */
export type MembershipPlanChangeDirection = 'upgrade' | 'downgrade';

/**
 * Price of the member's next charge: `/me.priceMinor`, which is the scheduled
 * plan's price once a switch is scheduled for the period end, else the plan's.
 * Not the price of the current plan — that is `membership.plan.priceMinor`.
 */
export const nextChargePriceMinor = (
  membership: Pick<Membership, 'priceMinor' | 'plan'>,
): number => membership.priceMinor ?? membership.plan.priceMinor;

/**
 * Our estimate of what an upgrade to `target` charges now, in minor units:
 * the price difference for the share of the paid period left. `0` in a trial
 * (nothing is charged until it ends) or when `target` is not dearer; `null`
 * without a known period. The backend's own proration may differ slightly.
 */
export const upgradeChargeEstimateMinor = (
  membership: Membership,
  target: MembershipPlan,
  now = Date.now(),
): number | null => {
  if (membership.status === 'trialing') {
    return 0;
  }
  const difference = target.priceMinor - membership.plan.priceMinor;
  if (difference <= 0) {
    return 0;
  }
  const start = Date.parse(membership.currentPeriodStart ?? '');
  const end = Date.parse(membership.currentPeriodEnd ?? '');
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return null;
  }
  const left = Math.min(Math.max(end - now, 0), end - start);
  return Math.round((difference * left) / (end - start));
};

/**
 * Our guess for the confirmation copy: a plan dearer than the member's
 * current plan is an upgrade, as the backend decides it. The result banner
 * still follows the answer (`appliedPlanChange`).
 */
export const planChangeDirection = (
  membership: Membership,
  target: Pick<MembershipPlan, 'priceMinor'>,
): MembershipPlanChangeDirection =>
  target.priceMinor > membership.plan.priceMinor ? 'upgrade' : 'downgrade';

/**
 * The company's other plan a member whose plan misses the current courier
 * zone is offered: the plan covering `zoneId` that is an upgrade; `null` when
 * the plan cannot be changed or no plan qualifies.
 */
export const clubUpgradePlan = (
  membership: Membership,
  plans: readonly MembershipPlan[],
  zoneId: string | null,
): MembershipPlan | null =>
  zoneId !== null && canChangePlan(membership)
    ? (plans.find(
        (plan) =>
          coversZone(plan, zoneId) &&
          planChangeDirection(membership, plan) === 'upgrade',
      ) ?? null)
    : null;

/** What the backend did with a switch to `targetId`; `null` when the membership shows neither. */
export const appliedPlanChange = (
  membership: Membership,
  targetId: string,
): MembershipPlanChangeDirection | null => {
  if (membership.plan.id === targetId) {
    return 'upgrade';
  }
  return membership.scheduledChange?.planId === targetId ? 'downgrade' : null;
};

/** A request of the member card. */
export type MembershipManageOperation = 'cancel' | 'changePlan' | 'keepPlan';

/**
 * Why a `/me/*` request failed. These endpoints have their own table: a 404
 * is "no membership" and a 409 one of three conflicts, unlike the purchase.
 * `unknown_outcome` — no answer, a server fault or a 503 — means the request
 * may have gone through and must be checked through `/me`, never resent;
 * `payment_rejected` — `MEMBERSHIP_PAYMENT_REJECTED` — means the payment service refused it and
 * nothing was charged.
 */
export type MembershipManageErrorKind =
  | 'not_found'
  | 'not_cancellable'
  | 'plan_change_not_applicable'
  | 'upgrade_requires_payment'
  | 'conflict'
  | 'card_refused'
  | 'payment_rejected'
  | 'unauthorized'
  | 'rejected'
  | 'unknown_outcome';

const MANAGE_KIND_BY_CODE: Record<string, MembershipManageErrorKind> = {
  MEMBERSHIP_NOT_FOUND: 'not_found',
  MEMBERSHIP_NOT_CANCELLABLE: 'not_cancellable',
  MEMBERSHIP_PLAN_CHANGE_NOT_APPLICABLE: 'plan_change_not_applicable',
  MEMBERSHIP_UPGRADE_REQUIRES_PAYMENT: 'upgrade_requires_payment',
  MEMBERSHIP_CARD_REFUSED: 'card_refused',
};

const MANAGE_KIND_BY_PAYMENT_FAULT: Record<
  MembershipPaymentFault,
  MembershipManageErrorKind
> = {
  unknown: 'unknown_outcome',
  rejected: 'payment_rejected',
};

const MANAGE_KIND_BY_STATUS: Record<number, MembershipManageErrorKind> = {
  401: 'unauthorized',
  404: 'not_found',
  409: 'conflict',
  422: 'card_refused',
};

/**
 * Classifies a failed `/me/*` request. A payment service fault (`paymentFaultOf`)
 * comes first, then the backend code, then the HTTP status.
 */
export const manageErrorKind = (
  status: number | undefined,
  code: string | undefined,
): MembershipManageErrorKind => {
  const fault = paymentFaultOf(status, code);
  if (fault) {
    return MANAGE_KIND_BY_PAYMENT_FAULT[fault];
  }
  const byCode =
    code !== undefined && Object.hasOwn(MANAGE_KIND_BY_CODE, code)
      ? MANAGE_KIND_BY_CODE[code]
      : undefined;
  if (byCode) {
    return byCode;
  }
  if (status === undefined) {
    return 'unknown_outcome';
  }
  const byStatus = MANAGE_KIND_BY_STATUS[status];
  if (byStatus) {
    return byStatus;
  }
  return status >= 500 ? 'unknown_outcome' : 'rejected';
};

/**
 * A failed `/me/*` request. Carries only its classification and what the
 * backend said about the payment service, never the request or the response,
 * so no payment field can reach a report through it.
 */
export class MembershipManageError extends Error {
  readonly kind: MembershipManageErrorKind;
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly upstream: MembershipUpstream | undefined;

  constructor(
    kind: MembershipManageErrorKind,
    status?: number,
    code?: string,
    upstream?: MembershipUpstream,
  ) {
    super(`Membership request failed: ${kind}`);
    this.name = 'MembershipManageError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.upstream = upstream;
  }
}

/** Anything a `/me/*` request threw, as a `MembershipManageError`; the unknown is an unknown outcome. */
export const asManageError = (error: unknown): MembershipManageError =>
  error instanceof MembershipManageError
    ? error
    : new MembershipManageError('unknown_outcome');

/** Why a cancel, a plan switch or "Keep current plan" did not go through. */
export interface MembershipManageFailure {
  operation: MembershipManageOperation;
  /**
   * `declined`: the card was refused for an upgrade; `rejected`: the payment
   * service refused the upgrade charge, nothing was charged; `confirming`:
   * the upgrade charge has an unknown outcome and `/me` does not show it yet;
   * `failed`: anything else.
   */
  reason: 'declined' | 'rejected' | 'confirming' | 'failed';
}

const MANAGE_FAILURE_KEYS: Record<MembershipManageFailure['reason'], string> = {
  declined: 'membership:planChangeDeclined',
  rejected: 'membership:paymentNothingCharged',
  confirming: 'membership:planChangeConfirming',
  failed: 'membership:manageFailed',
};

/** Message of a failed request, shown where it was asked for. */
export const manageFailureLine = (
  failure: MembershipManageFailure,
): MembershipLine => ({
  key: MANAGE_FAILURE_KEYS[failure.reason],
  tone: 'warning',
});

/** A line of the switch copy; without a known date it uses the `…NoDate` variant, which promises none. */
const switchLine = (
  key: string,
  params: { planName: string; price: string },
  date: string | null,
): MembershipLine =>
  date ? { key, params: { ...params, date } } : { key: `${key}NoDate`, params };

/** Text of the plan switch confirmation, by our upgrade guess and the member's status. */
export const planChangeSheetLine = (
  membership: Membership,
  target: MembershipPlan,
  f: MembershipFormatters,
): MembershipLine => {
  const params = {
    planName: target.name,
    price: f.money(target.priceMinor, target.currency),
  };
  if (planChangeDirection(membership, target) === 'downgrade') {
    return switchLine(
      'membership:downgradeText',
      params,
      f.date(periodEnd(membership)),
    );
  }
  if (membership.status === 'trialing') {
    return switchLine(
      'membership:upgradeTrialText',
      params,
      f.date(membership.trialEnd),
    );
  }
  return switchLine(
    'membership:upgradeActiveText',
    params,
    f.date(membership.nextChargeAt ?? membership.currentPeriodEnd),
  );
};

/** Payload of `Membership: Cancel Clicked` and `Membership: Cancelled`. */
export interface MembershipCancelPayload {
  planCode: string;
  /** Status the membership was cancelled from. */
  status: MembershipStatus;
}

/** Where an upgrade started: the cart, the checkout or the club page. */
export type MembershipPlanChangeSource = 'cart' | 'checkout' | 'club_page';

/** Payload of `Membership: Plan Change Clicked` and `Membership: Plan Changed`. */
export interface MembershipPlanChangePayload {
  planCode: string;
  toPlanCode: string;
  /** Our guess on click; what the backend did on `Plan Changed`. */
  direction: MembershipPlanChangeDirection;
  /** Entry point of an upgrade offered for the current address; absent for a plain switch. */
  source?: MembershipPlanChangeSource;
}

/** Payload of `Membership: Plan Change Kept`. */
export interface MembershipPlanChangeKeptPayload {
  planCode: string;
  /** Plan of the switch that was called off; `null` when it was already gone. */
  scheduledPlanCode: string | null;
}

/** Events of the member card requests; mapped to analytics by the composition root. */
export type MembershipManageEvent =
  | { name: 'cancelled'; payload: MembershipCancelPayload }
  | { name: 'planChanged'; payload: MembershipPlanChangePayload }
  | { name: 'planChangeKept'; payload: MembershipPlanChangeKeptPayload };

/** Something about the member card requests or the "Add card" sheet (`?modal=club-card`) worth a Sentry event; never a payment field. */
export type MembershipManageIncident =
  | {
      reason: 'manageFailed';
      operation: MembershipManageOperation;
      kind: MembershipManageErrorKind;
      status?: number;
      code?: string;
      upstream?: MembershipUpstream;
    }
  | {
      reason: 'cardRefused';
      flow: 'planChange' | 'cardChange';
      status?: number;
      code?: string;
    }
  | {
      reason: 'cardUpdateFailed';
      stage: 'submit' | 'confirm';
      kind: MembershipManageErrorKind;
      status?: number;
      code?: string;
      upstream?: MembershipUpstream;
    };
